import { forwardRef, useState, type ReactElement, type Ref } from 'react'
import {
  Box,
  Button,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Slide,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { TransitionProps } from '@mui/material/transitions'
import { Fullbleed } from '@dhl/components'
import { Close } from '@dhl/icons'
import { useIsSmallScreen, useTimeWindows } from '~/hooks'
import { TimeWindowDtoUtil, type CountryCode, type Timestamp, type TimeWindowDto } from '~/model'
import { borders } from '~/components'
import { formatTime, getNextBusinessDay, WeekDay } from '~/helpers'
import { Alert } from './Alert'
import { useFields } from './useFields'
import { DateSelector } from './DateSelector'
import { TimeWindowSelector } from './TimeWindowSelector'

type Props = {
  dateFieldName: string
  timeWindowStartFieldName?: string
  timeWindowEndFieldName?: string
  countryCode: CountryCode
  postalCode: string
  useEveningTimeWindows: boolean
  excludedWeekDays: WeekDay[]
  onClose: () => void
}

const Transition = forwardRef((props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) => (
  <Slide direction="up" ref={ref} {...props} />
))

export const Dialog = ({
  dateFieldName,
  timeWindowStartFieldName,
  timeWindowEndFieldName,
  countryCode,
  postalCode,
  useEveningTimeWindows,
  excludedWeekDays,
  onClose,
}: Props) => {
  // Hooks:
  const isSmallScreen = useIsSmallScreen()
  const { t } = useTranslation()
  const { timeWindows, isLoading, noTimeWindowsFound } = useTimeWindows(countryCode, postalCode, useEveningTimeWindows)
  const { setDateValue, setTimeWindowStartValue, setTimeWindowEndValue } = useFields(
    dateFieldName,
    timeWindowStartFieldName,
    timeWindowEndFieldName
  )

  // State:
  const nextPickupDate = getNextBusinessDay(excludedWeekDays)
  const [selectedDateIso, setSelectedDateIso] = useState<Timestamp | undefined>(nextPickupDate.toISOString())
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<TimeWindowDto | undefined>(undefined)

  // Derived:
  const enableTimeWindows = !!timeWindowStartFieldName && !!timeWindowEndFieldName
  const isValid = selectedDateIso && (!enableTimeWindows || selectedTimeWindow)
  const filteredTimeWindows = TimeWindowDtoUtil.filterByDate(timeWindows, selectedDateIso)

  const [datePanelExpanded, setDatePanelExpanded] = useState(true)
  const [timeWindowPanelExpanded, setTimeWindowPanelExpanded] = useState(enableTimeWindows)

  const handleSelectDate = (sdi: Timestamp) => {
    setSelectedDateIso(sdi)
    setSelectedTimeWindow(undefined)
    setDatePanelExpanded(false)
    setTimeWindowPanelExpanded(true)
  }

  const handleSelectTimeWindow = (stw: TimeWindowDto) => {
    setSelectedTimeWindow(stw)
    setDatePanelExpanded(false)
  }

  const handleApply = async () => {
    await setDateValue(selectedDateIso!)
    if (selectedTimeWindow) {
      await setTimeWindowStartValue(formatTime(selectedTimeWindow.startTime))
      await setTimeWindowEndValue(formatTime(selectedTimeWindow.endTime))
    }
    onClose()
  }

  return (
    <MuiDialog
      open
      maxWidth={false}
      fullWidth={false}
      slots={{ transition: isSmallScreen ? Transition : undefined }}
      onClose={onClose}
    >
      <Box
        sx={{
          padding: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4">{t('pickupMoment')}</Typography>
        <Fullbleed>
          <IconButton onClick={onClose} size="large">
            <Close color="action" />
          </IconButton>
        </Fullbleed>
      </Box>
      <DialogContent
        sx={({ palette: { grey } }) => ({
          ...borders(['bottom'], grey[300]),
          padding: '0px !important',
          position: 'relative',
        })}
      >
        {isLoading ? (
          <LinearProgress sx={{ position: 'absolute', width: '100%' }} />
        ) : (
          <Grid container direction={isSmallScreen ? 'column' : 'row'} gap={1}>
            <Grid>
              <DateSelector
                // data:
                selectedDateIso={selectedDateIso}
                timeWindows={timeWindows}
                // config:
                enableTimeWindows={enableTimeWindows}
                expanded={datePanelExpanded || !isSmallScreen}
                // events:
                onSelectDate={handleSelectDate}
                onToggleExpand={(expand) => {
                  setDatePanelExpanded(expand)
                  if (expand) {
                    setTimeWindowPanelExpanded(false)
                  }
                }}
              />
            </Grid>
            {enableTimeWindows && (
              <Grid>
                <TimeWindowSelector
                  // data:
                  selectedTimeWindow={selectedTimeWindow}
                  timeWindows={filteredTimeWindows}
                  // config:
                  showNoDateLabel={!selectedDateIso}
                  expanded={timeWindowPanelExpanded || !isSmallScreen}
                  // events:
                  onSelectTimeWindow={handleSelectTimeWindow}
                  onToggleExpand={(expand) => {
                    setTimeWindowPanelExpanded(expand)
                    if (expand) {
                      setDatePanelExpanded(false)
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}
        {enableTimeWindows && noTimeWindowsFound && (
          <Alert countryCode={countryCode} postalCode={postalCode} onClose={onClose} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" sx={{ padding: 1 }}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
          disabled={!isValid}
          sx={{
            paddingTop: 1,
            paddingBottom: 1,
            paddingLeft: 2,
            paddingRight: 2,
          }}
          data-test="pickup-date-select-button"
        >
          {t('select')}
        </Button>
      </DialogActions>
    </MuiDialog>
  )
}
